import React, { useState } from 'react';
import { Languages, ArrowUpRight, Clock, Plug, GraduationCap, Phone, ChevronDown, Check } from 'lucide-react';
import { supportedLanguages } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  Languages: Languages,
  ArrowUpRight: ArrowUpRight,
  Clock: Clock,
  Plug: Plug,
  GraduationCap: GraduationCap,
  Phone: Phone,
};

const CoreCapabilities = () => {
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { t } = useLanguage();

  // Get translated capabilities features
  const capabilitiesData = t('capabilities');
  const features = capabilitiesData?.features || [];

  return (
    <section className="bg-[#0a0a0b] py-20">
      <div className="max-w-6xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-teal-400 text-sm font-medium">{t('capabilities.title')}</span>
          <h2 className="text-3xl md:text-4xl font-semibold text-white mt-3">
            {t('capabilities.heading')} <span className="text-teal-400">{t('capabilities.voiceAI')}</span> ?
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
            {t('capabilities.subtitle')}
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(features) && features.map((capability, index) => {
            const IconComponent = iconMap[capability.icon];
            const isLanguageCard = capability.icon === 'Languages';
            
            return (
              <div
                key={index}
                className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-teal-400/30 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-teal-400/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-400/20 transition-colors">
                  {IconComponent && <IconComponent className="w-6 h-6 text-teal-400" />}
                </div>
                <h3 className="text-white text-lg font-medium mb-2">{capability.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-3">{capability.description}</p>
                
                {/* Language Dropdown for Multi-Language card */}
                {isLanguageCard && (
                  <div className="relative mt-4">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="w-full flex items-center justify-between bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-sm text-gray-300 hover:border-teal-400/50 transition-colors"
                    >
                      <span>{selectedLanguage ? selectedLanguage.name : t('capabilities.selectLanguage')}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isDropdownOpen && (
                      <div className="absolute z-50 w-full mt-2 bg-[#1a1a1f] border border-gray-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                        {supportedLanguages.map((lang, langIndex) => (
                          <button
                            key={langIndex}
                            onClick={() => {
                              setSelectedLanguage(lang);
                              setIsDropdownOpen(false);
                            }}
                            className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-300 hover:bg-teal-400/10 hover:text-teal-400 transition-colors"
                          >
                            <span className="flex items-center gap-3">
                              <span className="text-lg">{lang.flag}</span>
                              <span>{lang.name}</span>
                              <span className="text-gray-500">({lang.native})</span>
                            </span>
                            {selectedLanguage?.code === lang.code && (
                              <Check className="w-4 h-4 text-teal-400" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CoreCapabilities;
